import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { API } from "../../../App";
import { Plane } from "lucide-react";

// Registos de voo que os pilotos enviaram e esperam validação. Validar cria o voo (conta para as horas).
const dataPt = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "—");
const hhmm = (min) => `${Math.floor(min / 60)}:${String(min % 60).padStart(2, "0")}`;
// Hora de Lisboa de um instante em segundos UTC (as reservas guardam o início e o fim assim)
const horaLisboa = (ts) => new Date(ts * 1000).toLocaleTimeString("pt-PT", { timeZone: "Europe/Lisbon", hour: "2-digit", minute: "2-digit" });
const AVISO = {
  sem_reserva: "sem reserva",
  conta_horas_diferente: "conta-horas diferente das horas",
  conta_horas_descontinuo: "conta-horas sem continuidade",
  repetido: "registo repetido",
  duracao_longa: "mais de 12 horas",
  data_futura: "data no futuro",
};
// Fotos do conta-horas que o piloto juntou (horas e minutos, na descolagem e na aterragem)
const FOTOS = [["inicio_horas", "descolagem, horas"], ["inicio_minutos", "descolagem, minutos"], ["fim_horas", "aterragem, horas"], ["fim_minutos", "aterragem, minutos"]];
const erro = (e) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Não foi possível concluir.");

export default function PendingFlightRequests() {
  const [pedidos, setPedidos] = useState([]);
  const [diario, setDiario] = useState({});     // {id: {navigation_log_number, service_number}}
  const [recusando, setRecusando] = useState(null);
  const [nota, setNota] = useState("");
  const [aGravar, setAGravar] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/di/pedidos-voo/pendentes`, { withCredentials: true });
      setPedidos(data || []);
    } catch {
      setPedidos([]);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const campo = (id, chave, valor) => setDiario({ ...diario, [id]: { ...(diario[id] || {}), [chave]: valor } });

  const validar = async (p) => {
    const d = diario[p.id] || {};
    if (!d.navigation_log_number?.trim() || !d.service_number?.trim()) {
      toast.error("Indique o nº do diário e o nº de serviço antes de validar.");
      return;
    }
    setAGravar(true);
    try {
      await axios.post(`${API}/di/pedidos-voo/${p.id}/validar`,
        { navigation_log_number: d.navigation_log_number.trim(), service_number: d.service_number.trim() },
        { withCredentials: true });
      toast.success(p.pessoa_kind === "instructor" ? "Voo validado. Fica na caderneta do instrutor." : "Voo validado. Conta para as horas do piloto.");
      carregar();
    } catch (e) {
      toast.error(erro(e));
    } finally {
      setAGravar(false);
    }
  };

  const recusar = async (id) => {
    if (nota.trim().length < 3) {
      toast.error("Escreva o motivo da recusa (mínimo 3 caracteres).");
      return;
    }
    setAGravar(true);
    try {
      await axios.post(`${API}/di/pedidos-voo/${id}/recusar`, { nota: nota.trim() }, { withCredentials: true });
      toast.success("Registo recusado. O piloto vê o motivo.");
      setRecusando(null);
      setNota("");
      carregar();
    } catch (e) {
      toast.error(erro(e));
    } finally {
      setAGravar(false);
    }
  };

  if (pedidos.length === 0) return null;

  return (
    <Card className="instrument-panel" data-testid="di-voos-pendentes">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2 text-sky-300">
          <Plane className="w-5 h-5" /> Voos registados pelos pilotos para validar ({pedidos.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-xs">
        {pedidos.map((p) => (
          <div key={p.id} className="border-t border-slate-800 pt-2 space-y-2" data-testid="di-voo-pendente">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-slate-200">
                <span className="font-medium">{p.pilot_name || "Piloto"}</span>{p.pessoa_kind === "instructor" && <span className="text-slate-400"> (instrutor, {p.flight_type === "instrucao" ? `instrução${p.aluno_name ? ` a ${p.aluno_name}` : ""}` : "particular"})</span>} · {dataPt(p.flight_date)} · {p.aircraft_registration} · {p.departure_time}–{p.landing_time} ({hhmm(p.duration_minutes)})
                {p.counter_start && <span className="text-slate-400"> · conta-horas {p.counter_start} → {p.counter_end}</span>}
                {p.aerodrome && <span className="text-slate-400"> · {p.aerodrome}</span>}
                {/* Reserva que cobre o voo, procurada agora (pode ter sido feita ou cancelada depois do registo) */}
                {p.reserva
                  ? <span className="text-emerald-300" data-testid="di-voo-reserva"> · reserva {horaLisboa(p.reserva.start_ts)}–{horaLisboa(p.reserva.end_ts)}</span>
                  : <span className="text-amber-300" data-testid="di-voo-sem-reserva"> · sem reserva</span>}
              </span>
              {p.avisos_confirmados?.length > 0 && (
                <span className="text-amber-300">Avisos confirmados: {p.avisos_confirmados.map((c) => AVISO[c] || c).join(", ")}</span>
              )}
            </div>
            {p.fotos && Object.keys(p.fotos).length > 0 && (
              <div className="text-slate-300" data-testid="di-voo-fotos">
                Fotos do conta-horas:{" "}
                {FOTOS.filter(([m]) => p.fotos[m]).map(([m, rotulo], i) => (
                  <span key={m}>{i > 0 && " · "}<a href={`${API}/di/pedidos-voo/${p.id}/fotos/${m}`} target="_blank" rel="noreferrer" className="text-sky-300 underline">{rotulo}</a></span>
                ))}
              </div>
            )}
            {/* Continuidade do conta-horas, calculada agora: faltam horas entre este voo e o anterior/seguinte da aeronave */}
            {p.continuidade?.length > 0 && (
              <div className="space-y-0.5 text-amber-300" data-testid="di-voo-continuidade">
                {p.continuidade.map((texto) => <p key={texto}>{texto}</p>)}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Input value={diario[p.id]?.navigation_log_number || ""} onChange={(e) => campo(p.id, "navigation_log_number", e.target.value)} placeholder="Nº do diário" className="bg-slate-800 border-slate-700 w-36" data-testid="di-voo-diario" />
              <Input value={diario[p.id]?.service_number || ""} onChange={(e) => campo(p.id, "service_number", e.target.value)} placeholder="Nº de serviço" className="bg-slate-800 border-slate-700 w-36" data-testid="di-voo-servico" />
              <Button size="sm" onClick={() => validar(p)} disabled={aGravar} data-testid="di-voo-validar">Validar</Button>
              <Button size="sm" variant="outline" onClick={() => { setRecusando(recusando === p.id ? null : p.id); setNota(""); }} data-testid="di-voo-recusar">Recusar</Button>
            </div>
            {recusando === p.id && (
              <div className="flex flex-wrap items-center gap-2">
                <Input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Motivo da recusa" className="bg-slate-800 border-slate-700 flex-1 min-w-0" data-testid="di-voo-nota" />
                <Button size="sm" variant="destructive" onClick={() => recusar(p.id)} disabled={aGravar}>Confirmar recusa</Button>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
